// src/components/CreateNewPassword.tsx

import { useState } from "react";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "../ui/form";

import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { authControllerResetPassword } from "@/api/types/main";

interface CreateNewPasswordProps {
  token: string;
}

export const CreateNewPassword = ({ token }: CreateNewPasswordProps) => {
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordSchema = z
    .object({
      password: z
        .string()
        .min(6, { message: "Пароль должен содержать не менее 6 символов." })
        .max(32, { message: "Пароль не может превышать 32 символа." }),
      confirmPassword: z.string().min(6, {
        message: "Подтверждение пароля должно содержать не менее 6 символов.",
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      path: ["confirmPassword"],
      message: "Пароли не совпадают.",
    });

  type PasswordFormValues = z.infer<typeof passwordSchema>;

  const form = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const { mutate: sendNewPassword, isPending } = useMutation({
    mutationFn: async (data: { newPassword: string }) => {
      return await authControllerResetPassword(token, data);
    },
    onError: (error: AxiosError<{ type: string; error: string }>) => {
      if (error.response?.data?.type === "bad-password") {
        form.setError("password", {
          type: "manual",
          message: error.response?.data?.error,
        });
      } else {
        setFormError("Произошла ошибка при сбросе пароля. Попробуйте снова.");
      }
    },
    onSuccess: () => {
      navigate("/login");
    },
  });

  const onSubmit = (data: PasswordFormValues) => {
    setFormError(null);
    form.clearErrors();
    sendNewPassword({ newPassword: data.password });
  };

  return (
    <div className="flex h-screen justify-center items-center">
      <Card className="w-full max-w-sm px-4">
        <CardHeader>
          <CardTitle className="text-2xl">Сброс пароля</CardTitle>
          <CardDescription>Введите новый пароль</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
              {formError && <div className="text-red-600 text-center">{formError}</div>}

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Пароль</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute inset-y-0 right-2 flex items-center"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-600" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-600" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormDescription>Пароль от 6 до 32 символов</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Подтвердите пароль</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          className="absolute inset-y-0 right-2 flex items-center"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-gray-600" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-600" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormDescription>Введите пароль ещё раз для проверки</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button className="w-full" type="submit" disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Загрузка...
                  </>
                ) : (
                  "Сбросить пароль"
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex-col gap-2">
          <Button variant="outline" className="w-full" onClick={() => navigate("/registration")}>
            Зарегистрироваться
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};
